
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReadingStats } from '../types';
import { Trophy, Clock, Book, Activity, TrendingUp, User, Library, Cloud, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { UserSettings } from '../components/UserSettings';
import { useUserProfile } from '../hooks/useUserProfile';
import { useFeatureGating } from '../hooks/useFeatureGating';
import { usePageMeta } from '../hooks/usePageMeta';
import { MembershipTier } from '../types/membership';
import XPProgressBar from '../components/XPProgressBar';
import StreakDisplay from '../components/StreakDisplay';
import DailyChallenges from '../components/DailyChallenges';
import { supabase } from '../services/supabaseClient';
import { manualSync } from '../services/syncService';

interface ProfilePageProps {
  stats: ReadingStats;
  currentWpm: number;
  userTier?: MembershipTier;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ stats, currentWpm, userTier }) => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Reading Progress Tracker & WPM Tracker | Flash-Read',
    description: 'Track your reading progress, WPM stats, XP, streaks, and daily challenges. View detailed session history and performance charts. Your personal reading dashboard.',
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { hasCloudSync } = useFeatureGating();

  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [syncMessage, setSyncMessage] = useState('');
  const [lastSync, setLastSync] = useState<string | null>(null);

  // Load last sync time on mount
  useEffect(() => {
    const last = localStorage.getItem('flashread_last_sync');
    setLastSync(last);
  }, []);

  const handleUpgradeClick = () => {
    if (!hasCloudSync()) {
      setShowUpgradeModal(true);
    }
  };

  const handleSync = async () => {
    if (isSyncing) return;

    // Only Ultra users can sync
    if (userTier !== 'ultra') {
      handleUpgradeClick();
      return;
    }

    setIsSyncing(true);
    setSyncStatus('idle');
    setSyncMessage('Syncing your data...');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setSyncStatus('error');
        setSyncMessage('You must be logged in to sync.');
        return;
      }

      const status = await manualSync(user.id, userTier);

      if (status === 'synced') {
        const now = new Date().toISOString();
        setLastSync(now);
        localStorage.setItem('flashread_last_sync', now);
        setSyncStatus('success');
        setSyncMessage('All data synced with cloud!');
      } else if (status === 'error') {
        setSyncStatus('error');
        setSyncMessage('Sync failed. Please try again.');
      } else {
        setSyncStatus('idle');
        setSyncMessage('Sync pending...');
      }
    } catch (err) {
      setSyncStatus('error');
      setSyncMessage('An unexpected error occurred during sync.');
    } finally {
      setIsSyncing(false);

      // Clear status message after 5 seconds
      setTimeout(() => {
        setSyncStatus('idle');
        setSyncMessage('');
      }, 5000);
    }
  };

  // Prepare chart data from history
  const chartData = (stats.history || []).slice(-10).map((session) => {
    const date = new Date(session.date);
    const label = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
    return {
      session: label,
      wpm: session.avgWpm,
      fullDate: date.toLocaleString()
    };
  });

  const displayData = chartData.length > 0 ? chartData : [
    { session: 'Start', wpm: 0 }
  ];

  return (
    <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
      {/* Header with User Info */}
      <div className="flex items-center justify-between mb-10">
        <div className="text-center flex-1">
          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">Your Reading</span> Dashboard
          </h1>
          <div className="stories-intro mb-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-100 dark:border-blue-800 max-w-3xl mx-auto">
            <p className="text-base md:text-lg text-slate-700 dark:text-slate-300 leading-relaxed text-center">
              Your personal <span className="keyword-highlight">reading progress tracker</span> and <span className="keyword-highlight">WPM tracker</span>.<br />
              View detailed <span className="keyword-highlight">reading statistics</span> and <span className="keyword-highlight">reading dashboard</span> insights.<br />
              Monitor your <span className="keyword-highlight">reading level progress</span> and review <span className="keyword-highlight">session history</span>.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Sync Progress Button */}
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isSyncing
                ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-wait'
                : userTier === 'ultra'
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
            title={userTier === 'ultra' ? 'Sync your reading progress across devices' : 'Upgrade to Ultra to use Cloud Sync'}
          >
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Cloud className="h-4 w-4" />
            )}
            <span>{isSyncing ? 'Syncing...' : 'Sync Progress'}</span>
          </button>

          {/* Sync Status */}
          {syncMessage && (
            <div className={`flex items-center gap-1.5 text-xs ${
              syncStatus === 'success' ? 'text-green-600 dark:text-green-400' :
              syncStatus === 'error' ? 'text-red-600 dark:text-red-400' :
              'text-gray-500 dark:text-gray-400'
            }`}>
              {syncStatus === 'success' && <CheckCircle className="h-3 w-3" />}
              {syncStatus === 'error' && <AlertCircle className="h-3 w-3" />}
              {syncStatus === 'idle' && <RefreshCw className="h-3 w-3" />}
              <span>{syncMessage}</span>
            </div>
          )}

          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showSettings
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <User className="h-4 w-4" />
            Settings
          </button>
        </div>

        {/* Upgrade Modal */}
        {showUpgradeModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Cloud className="h-8 w-8 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">Upgrade to Ultra</h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Get Cloud Sync to access your reading progress from any device. Never lose your progress again!
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Sync progress across all devices</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Automatic cloud backup</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span>Plus all Ultra features</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="flex-1 px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowUpgradeModal(false);
                    navigate('/signup');
                  }}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
                >
                  View Plans
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content Grid: Stats/Settings (2/3) + Challenges (1/3) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12">
        {/* Left Column: Stats or Settings */}
        <div className="lg:col-span-2">
          {/* User Settings - shown when Account is open */}
          {showSettings && (
            <div className="p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 mb-6">
              <UserSettings />
            </div>
          )}

          {/* Library Access Card - always visible */}
          <div
            className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl border border-blue-200 dark:border-blue-800 mb-6 cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate('/library')}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/40 rounded-xl flex items-center justify-center">
                  <Library className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Content Library</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Import files, add your own content</p>
                </div>
              </div>
              <div className="flex items-center gap-1 text-blue-600 dark:text-blue-400 font-medium">
                <span>Go to Library</span>
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* XP Progress and Streak */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Your Progress</h3>
              <XPProgressBar currentXP={stats.wordsRead} totalXP={stats.wordsRead * 2} />
            </div>

            <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Reading Streak</h3>
              <StreakDisplay currentStreak={Math.min(Math.floor(stats.sessions / 3), 30)} longestStreak={Math.min(Math.floor(stats.sessions / 2), 100)} />
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <StatCard
              icon={Activity}
              label="Current Speed"
              value={`${currentWpm}`}
              sub="WPM"
              color="text-blue-600 dark:text-blue-400"
              bg="bg-blue-50 dark:bg-blue-900/30"
            />
            <StatCard
              icon={Book}
              label="Words Consumed"
              value={(stats.wordsRead / 1000).toFixed(1) + 'k'}
              sub="Total Words"
              color="text-purple-600 dark:text-purple-400"
              bg="bg-purple-50 dark:bg-purple-900/30"
            />
            <StatCard
              icon={Clock}
              label="Time Trained"
              value={Math.round(stats.timeSpentSeconds / 60).toString()}
              sub="Minutes"
              color="text-emerald-600 dark:text-emerald-400"
              bg="bg-emerald-50 dark:bg-emerald-900/30"
            />
            <StatCard
              icon={Trophy}
              label="Sessions"
              value={stats.sessions.toString()}
              sub="Completed"
              color="text-amber-600 dark:text-amber-400"
              bg="bg-amber-50 dark:bg-amber-900/30"
            />
          </div>

          {/* Chart */}
          <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm transition-colors duration-300">
            <div className="flex items-center space-x-2 mb-8">
              <TrendingUp className="h-5 w-5 text-slate-400" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Speed Trend (Last 10 Sessions)</h3>
            </div>
            <div className="h-64 w-full">
              {displayData.length > 1 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={displayData}>
                    <XAxis
                      dataKey="session"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#94a3b8', fontSize: 12 }}
                      dy={10}
                    />
                    <YAxis hide />
                    <Tooltip
                      cursor={{ fill: '#f1f5f9', radius: 4 }}
                      contentStyle={{
                        borderRadius: '8px',
                        border: 'none',
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        backgroundColor: 'var(--tw-content-bg, #fff)',
                        color: 'var(--tw-content-color, #0f172a)'
                      }}
                    />
                    <Bar dataKey="wpm" radius={[4, 4, 0, 0]}>
                      {displayData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={index === displayData.length - 1 ? '#3b82f6' : '#94a3b8'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-slate-400">
                  <Activity className="h-10 w-10 mb-2 opacity-20" />
                  <p>Complete a session to see your progress chart.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Daily Challenges */}
        <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-200 dark:border-slate-700 h-fit">
          <DailyChallenges />
        </div>
      </div>
    </div>
  );
};

const StatCard: React.FC<{ icon: any, label: string, value: string, sub: string, color: string, bg: string }> = ({
  icon: Icon, label, value, sub, color, bg
}) => (
  <div className="bg-white dark:bg-slate-800 p-6 rounded-2xl border border-slate-100 dark:border-slate-700 shadow-sm hover:shadow-md transition-all duration-300">
    <div className={`w-12 h-12 ${bg} rounded-xl flex items-center justify-center mb-4`}>
      <Icon className={`h-6 w-6 ${color}`} />
    </div>
    <div className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</div>
    <div className="flex items-baseline space-x-2">
      <span className="text-3xl font-bold text-slate-900 dark:text-white">{value}</span>
      <span className="text-xs font-medium text-slate-400 dark:text-slate-500">{sub}</span>
    </div>
  </div>
);

export default ProfilePage;
